import * as Sentry from '@sentry/react';
import React, { ReactNode, useCallback, useRef } from 'react';
import { Form as BaseForm } from '../../atoms';
import { firebase, message } from '@vette/frontend-utils';
import { phone as phoneUtils } from '@vette/common-utils';
import { User } from 'firebase/auth';
import { Form, FormRef } from './Form';
import { FirebaseError } from 'firebase/app';
import {
  useUserWithPhoneExistsQuery,
  UserWithPhoneExistsQueryVariables,
  UserRoleEnum,
} from '@vette/data-access';
import { FormValues, FormValuesWithName } from './types';

type Props = {
  header: ReactNode;
} & (
  | {
      flow: 'login';
      userRole: UserRoleEnum;
      userNotFoundErrorMessage: string;
      onSuccess: (user: User, values: FormValues) => void;
    }
  | {
      flow: 'signup';
      onSuccess: (user: User, values: FormValuesWithName) => void;
    }
  | {
      flow: 'acceptInvitation';
      safeLogout: () => Promise<void>;
      onSuccess: (user: User, values: FormValuesWithName) => void;
    }
  | {
      flow: 'changePhone';
      phoneNumberLabel: string;
      onSuccess: (user: User, values: FormValues) => void;
    }
);

export const AuthForm: React.FC<Props> = ({ header, onSuccess, ...props }) => {
  const [form] = BaseForm.useForm<FormValuesWithName>();
  const formRef = useRef<FormRef>(null);

  const { loading: userWithPhoneExistsLoading, refetch: userWithPhoneExists } =
    useUserWithPhoneExistsQuery({ skip: true });

  const {
    wasPhoneNumberProvided,
    isVerificationLoading,
    setNewRecaptchaVerifierInstance,
    /* login, signup, acceptInvitation */
    authenticate,
    verifyCode,
    reset,
    /* changePhone */
    sendVerificationCode,
    updatePhone,
  } = firebase.usePhoneAuthentication();

  const onAuthenticate = useCallback(async () => {
    if (props.flow === 'acceptInvitation') await props.safeLogout();

    try {
      await setNewRecaptchaVerifierInstance();

      const { phone: _phone } = await form.validateFields(['phone']);
      const phone = phoneUtils.normalize(_phone);

      form.setFieldsValue({ verificationCode: '' });

      const { data } = await userWithPhoneExists({
        input: {
          phone,
          userRole: props.flow === 'login' ? props.userRole : undefined,
        },
      } satisfies UserWithPhoneExistsQueryVariables);

      const userWithProvidedNumberAlreadyExist = data.userWithPhoneExists;

      if (props.flow === 'login' && !userWithProvidedNumberAlreadyExist) {
        message.error(props.userNotFoundErrorMessage);
        return false;
      } else if (props.flow !== 'login' && userWithProvidedNumberAlreadyExist) {
        form.setFields([
          {
            name: 'phone',
            errors: ['User with this phone number already exists'],
          },
        ]);
        return false;
      }

      if (props.flow !== 'changePhone') {
        await authenticate(phone);
      } else {
        await sendVerificationCode(phone);
      }

      formRef.current?.focusVerificationCodeInput();
      return true;
    } catch (e) {
      console.error(e);
      Sentry.captureException(e);
      message.error('Too many attempts, please try again later.');
      return false;
    }
  }, [
    form,
    authenticate,
    userWithPhoneExists,
    setNewRecaptchaVerifierInstance,
    sendVerificationCode,
    props,
  ]);

  const onVerify = useCallback(async () => {
    const values = await form.validateFields();

    try {
      const user =
        props.flow !== 'changePhone'
          ? await verifyCode(values.verificationCode)
          : await updatePhone(values.verificationCode);

      onSuccess(user, { ...values, phone: phoneUtils.normalize(values.phone) });

      reset();
      form.resetFields();
    } catch (e) {
      if (
        e instanceof FirebaseError &&
        ['auth/user-disabled', 'auth/invalid-verification-id'].includes(e.code)
      ) {
        form.setFields([
          {
            name: 'verificationCode',
            errors: [
              'Your account has been disabled. Contact us at vetters@vette.io for assistance.',
            ],
          },
        ]);
      } else {
        form.setFields([
          { name: 'verificationCode', errors: ['Invalid verification code'] },
        ]);
      }
    }
  }, [form, verifyCode, updatePhone, onSuccess, reset, props]);

  return (
    <Form
      form={form}
      ref={formRef}
      header={header}
      onAuthenticate={onAuthenticate}
      onVerify={onVerify}
      isVerificationLoading={
        isVerificationLoading || userWithPhoneExistsLoading
      }
      verificationDisabled={!wasPhoneNumberProvided}
      buttonText={
        props.flow === 'login'
          ? 'Log In'
          : props.flow === 'signup'
          ? 'Sign Up'
          : props.flow === 'acceptInvitation'
          ? 'Join team'
          : props.flow === 'changePhone'
          ? 'Save new number'
          : ''
      }
      gatherName={props.flow === 'acceptInvitation' || props.flow === 'signup'}
      gatherChannel={props.flow === 'signup'}
      phoneNumberLabel={
        props.flow === 'changePhone' ? 'New phone number' : undefined
      }
    />
  );
};
